import type {
  EmpiConflictDetail,
  EmpiConflictOption,
  EmpiGoldenConfidence,
  EmpiGoldenSource,
  EmpiIdentityMatchBand,
  EmpiIdentityReason,
  EmpiResourceType,
} from "@/shared/types/empi";
import { formatDose } from "@/shared/lib/formatDose";

/**
 * Module 5 (EMPI) wording and field summaries shared by the patient's review
 * screens, the clinician's conflict panel and the golden-record tags. Every
 * backend code is turned into plain words here — no screen shows a raw enum.
 */

// The detail a "missing" / "only in a document" record check is about, in plain words.
const FIELD_WORDS: Record<string, string> = {
  clinical_status: "status", status: "status", value: "result", dose: "dose", frequency: "how often it's taken",
  criticality: "severity", reaction: "reaction",
};

/**
 * Why a record check asks the patient, in one plain sentence, from the
 * backend's conflict reason (cluster.conflict_reason: "; "-joined codes, some
 * with a ":field" suffix). Anything else reads as different values.
 */
export function conflictReasonText(reason: string | null | undefined, noun: string): string {
  const codes = String(reason ?? "").split(";").map((code) => code.trim());
  const field = (code: string) => FIELD_WORDS[code.split(":")[1] ?? ""] ?? "details";
  const missing = codes.find((code) => code.startsWith("critical_field_missing"));
  const documentOnly = codes.find((code) => code.startsWith("document_only_value"));

  if (missing) return `One of your records is missing the ${field(missing)}. Pick the record that's right, or tell your clinician it changed.`;
  if (documentOnly) return `Only your uploaded document lists the ${field(documentOnly)}. Pick the record that's right.`;
  if (codes.includes("allergy_fuzzy_match_unconfirmed")) {
    return "These look like the same allergy written under different names. Pick the one that's right, or tell your clinician if they're different allergies.";
  }
  if (codes.includes("unit_conversion_unavailable")) return "Your records use units we can't compare with each other. Pick the one you think is right.";
  if (codes.some((code) => code === "medication_status_conflict" || code.endsWith(":status") || code.endsWith(":clinical_status"))) {
    return `Your records show a different status for this ${noun}. Pick the one you think is right.`;
  }
  return `Your records show different values for this ${noun}. Pick the one you think is right.`;
}

/** IdentityReason → what the patient reads. Exhaustive: a new backend code is a type error. */
export const IDENTITY_REASON_TEXT: Record<EmpiIdentityReason, string> = {
  family_name_differs: "The last name is different from the one on your profile.",
  given_name_differs: "The first name is different from the one on your profile.",
  dob_day_month_transposed: "The birth date looks like it might have the day and month swapped.",
  dob_close: "The birth date is a day off from the one on your profile.",
  dob_mismatch: "The birth date doesn't match the one on your profile.",
  sex_differs: "The sex on the record doesn't match your profile.",
  no_contact_overlap: "None of the phone numbers, emails or addresses match yours.",
  no_comparable_demographics: "The record doesn't include enough details to compare with your profile.",
  dob_not_comparable: "We couldn't compare birth dates because one is missing. Adding your date of birth to your profile helps.",
  no_identifying_information: "The record doesn't include a name or date of birth to check.",
  no_identity_evidence_at_all: "The upload doesn't include any patient details to check.",
  patient_confirmed_same_person: "You confirmed this record is yours.",
  patient_confirmed_not_same_person: "You told us this record isn't yours.",
  patient_confirmed_same_details: "It shows exactly the same details as a record you confirmed is yours.",
  matches_a_name_you_confirmed: "It shows one of your names and your date of birth.",
  you_said_not_you_before: "You told us an earlier file with exactly these details isn't you, so we're asking this time.",
};

/** IdentityMatchBand → a one-line summary of how close the match was. */
export const MATCH_BAND_TEXT: Record<EmpiIdentityMatchBand, string> = {
  accept: "The details match your profile.",
  review: "We couldn't confirm these details match your profile.",
  reject: "Several details don't match your profile.",
};

/** ClusteredResourceType → the patient's word for it. */
export const RESOURCE_NOUN: Record<EmpiResourceType, string> = {
  Condition: "condition",
  MedicationRequest: "medicine",
  AllergyIntolerance: "allergy",
  Observation: "test result",
  Immunization: "vaccine",
  Procedure: "procedure",
  DiagnosticReport: "report",
  Encounter: "visit",
  FamilyMemberHistory: "family history entry",
  CarePlan: "care plan",
  ServiceRequest: "order",
  Coverage: "insurance",
};

export const RESOURCE_TITLE: Record<EmpiResourceType, string> = {
  Condition: "Condition",
  MedicationRequest: "Medication",
  AllergyIntolerance: "Allergy",
  Observation: "Test result",
  Immunization: "Vaccine",
  Procedure: "Procedure",
  DiagnosticReport: "Report",
  Encounter: "Visit",
  FamilyMemberHistory: "Family history",
  CarePlan: "Care plan",
  ServiceRequest: "Order",
  Coverage: "Insurance",
};

/** Where a value came from, in patient terms (never the raw tier number). */
export function trustText(source: Pick<EmpiConflictOption, "structured" | "sourceTier">): string {
  if (source.sourceTier === 1) return "Confirmed by your clinician";
  return source.structured ? "From your hospital's or clinic's records" : "From a scanned or uploaded document";
}

/** A source's name: the upload's title / file name, or "Epic sync" etc. */
export function sourceName(source: Pick<EmpiConflictOption, "sourceLabel" | "sourceVendor">): string {
  return source.sourceLabel || source.sourceVendor || "Unknown source";
}

/** GoldenConfidence + corroborating count → the record's one-line tag. */
export function goldenTag(confidence: EmpiGoldenConfidence, corroborating: number): { label: string; tone: "confirmed" | "seen" | "document" } {
  if (confidence === "unconfirmed_document") return { label: "From a document, not yet confirmed", tone: "document" };
  if (corroborating >= 2) return { label: `Seen in ${corroborating} sources`, tone: "seen" };
  return { label: "Confirmed by hospital record", tone: "confirmed" };
}

export function formatEmpiDate(value: string | null | undefined): string {
  if (!value) return "Not available";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const text = (v: unknown): string => (v === undefined || v === null ? "" : String(v).trim());
const firstText = (...values: unknown[]) => values.map(text).find(Boolean) ?? "";

/** A field key as a label ("clinical_status" → "Clinical status"). */
export function fieldLabel(key: string): string {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function dateText(value: unknown): string {
  const raw = firstText(value);
  return raw ? formatEmpiDate(raw) : "";
}

/** The values that matter for comparing one option with another, per resource type. */
export function optionSummary(resourceType: EmpiResourceType, fields: Record<string, unknown>): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = (() => {
    switch (resourceType) {
      case "Condition":
        return [
          { label: "Condition", value: text(fields.display_name) },
          { label: "Code", value: firstText(fields.icd10_code, fields.snomed_code) },
          { label: "Status", value: text(fields.clinical_status) },
          { label: "Started", value: firstText(fields.onset_date) && formatEmpiDate(firstText(fields.onset_date)) },
        ];
      case "AllergyIntolerance":
        return [
          { label: "Allergy", value: text(fields.display_name) },
          { label: "Reaction", value: firstText(fields.reaction_manifestation, fields.reaction_description) },
          { label: "Severity", value: text(fields.criticality) },
          { label: "Status", value: text(fields.clinical_status) },
        ];
      case "Observation": {
        const quantity = [text(fields.value_quantity), text(fields.value_unit)].filter(Boolean).join(" ");
        return [
          { label: "Test", value: text(fields.display_name) },
          { label: "Result", value: quantity || firstText(fields.value_string, fields.value_codeable_text) },
          { label: "Date", value: firstText(fields.effective_date, fields.issued) && formatEmpiDate(firstText(fields.effective_date, fields.issued)) },
          { label: "Interpretation", value: text(fields.interpretation) },
        ];
      }
      case "MedicationRequest": {
        const dose = formatDose(text(fields.dose_quantity), text(fields.dose_unit));
        return [
          { label: "Medicine", value: text(fields.medication_name) },
          { label: "Dose", value: dose || text(fields.dosage_text) },
          { label: "How often", value: text(fields.frequency) },
          { label: "Status", value: text(fields.status) },
        ];
      }
      case "Immunization":
        return [
          { label: "Vaccine", value: text(fields.vaccine_display) },
          { label: "Date", value: dateText(fields.occurrence_date) },
          { label: "Lot", value: text(fields.lot_number) },
          { label: "Status", value: text(fields.status) },
        ];
      case "Procedure":
        return [
          { label: "Procedure", value: text(fields.display_name) },
          { label: "Code", value: text(fields.cpt_code) },
          { label: "Date", value: dateText(firstText(fields.performed_date, fields.performed_start)) },
          { label: "Status", value: text(fields.status) },
        ];
      case "DiagnosticReport":
        return [
          { label: "Report", value: text(fields.display_name) },
          { label: "Date", value: dateText(firstText(fields.effective_date, fields.issued_date)) },
          { label: "Conclusion", value: text(fields.conclusion) },
          { label: "Status", value: text(fields.status) },
        ];
      case "Encounter":
        return [
          { label: "Visit", value: firstText(fields.encounter_type, fields.encounter_class_display) },
          { label: "Date", value: dateText(fields.start_date) },
          { label: "Where", value: text(fields.location_display) },
          { label: "Status", value: text(fields.status) },
        ];
      case "FamilyMemberHistory":
        return [
          { label: "Relative", value: firstText(fields.relationship_display, fields.member_name) },
          { label: "Condition", value: text(fields.condition_display) },
          { label: "Deceased", value: fields.deceased === true ? "Yes" : fields.deceased === false ? "No" : "" },
        ];
      case "CarePlan":
        return [
          { label: "Plan", value: firstText(fields.addresses_display, fields.goal_display) },
          { label: "Activity", value: text(fields.activity_code_text) },
          { label: "Status", value: text(fields.status) },
        ];
      case "ServiceRequest":
        return [
          { label: "Order", value: text(fields.code_text) },
          { label: "Date", value: dateText(firstText(fields.authored_on, fields.occurrence_date)) },
          { label: "Status", value: text(fields.status) },
        ];
      case "Coverage":
        return [
          { label: "Insurance", value: firstText(fields.payor_name, fields.plan_name) },
          { label: "Member ID", value: text(fields.subscriber_id) },
          { label: "Group", value: text(fields.group_number) },
          { label: "Status", value: text(fields.status) },
        ];
    }
  })();
  return rows.map((r) => ({ ...r, value: r.value || "Not specified" }));
}

type FieldRow = { label: string; value?: (fields: Record<string, unknown>) => string };

/**
 * Backend field key (resolve.conflict_fields) -> the comparison row it is
 * shown in. A key with `value` gets its own row when the type's usual rows
 * don't include it, so whatever a check is about is always on screen and
 * highlighted. Same keys as the backend's tests/empi/test_conflict_fields.py.
 */
export const CONFLICT_FIELD_ROWS: Record<EmpiResourceType, Record<string, FieldRow>> = {
  MedicationRequest: {
    rxcui: { label: "RxNorm code", value: (f) => text(f.rxcui) },
    dose: { label: "Dose" },
    frequency: { label: "How often" },
    status: { label: "Status" },
  },
  Condition: {
    icd10_code: { label: "Code" },
    snomed_code: { label: "SNOMED code", value: (f) => text(f.snomed_code) },
    clinical_status: { label: "Status" },
    verification_status: { label: "Confirmed", value: (f) => text(f.verification_status) },
  },
  AllergyIntolerance: {
    name: { label: "Allergy" },
    code: { label: "Code", value: (f) => text(f.code) },
    criticality: { label: "Severity" },
    reaction: { label: "Reaction" },
    clinical_status: { label: "Status" },
    verification_status: { label: "Confirmed", value: (f) => text(f.verification_status) },
  },
  Observation: {
    value: { label: "Result" },
    value_quantity: { label: "Result" },
    value_string: { label: "Result" },
    value_codeable_text: { label: "Result" },
  },
  Immunization: {
    cvx_code: { label: "Vaccine code", value: (f) => text(f.cvx_code) },
    lot_number: { label: "Lot" },
    dose: { label: "Dose", value: (f) => formatDose(text(f.dose_quantity), text(f.dose_unit)) },
    status: { label: "Status" },
  },
  Procedure: {
    cpt_code: { label: "Code" },
    status: { label: "Status" },
  },
  DiagnosticReport: {
    code: { label: "Code", value: (f) => text(f.code) },
    conclusion: { label: "Conclusion" },
    status: { label: "Status" },
  },
  Encounter: {
    encounter_class: { label: "Visit type", value: (f) => firstText(f.encounter_class_display, f.encounter_class) },
    end_date: { label: "Ended", value: (f) => dateText(f.end_date) },
    status: { label: "Status" },
  },
  FamilyMemberHistory: {
    condition_code: { label: "Condition code", value: (f) => text(f.condition_code) },
    relationship: { label: "Relative" },
    deceased: { label: "Deceased" },
  },
  CarePlan: {
    activity_status: { label: "Activity status", value: (f) => text(f.activity_status) },
    status: { label: "Status" },
  },
  ServiceRequest: {
    code: { label: "Order" },
    status: { label: "Status" },
  },
  Coverage: {
    group_number: { label: "Group" },
    plan_name: { label: "Plan", value: (f) => text(f.plan_name) },
    end_date: { label: "Ends", value: (f) => dateText(f.end_date) },
    status: { label: "Status" },
  },
};

/**
 * The comparison a record check shows: its rows, each option's values, and
 * the rows to highlight. Highlighted = the fields the backend says the check
 * is about (decided 2026-10-23: the same rule everywhere, never a question
 * with nothing marked). Without that list (an older backend) it falls back
 * to the rows whose shown values differ.
 */
export function conflictComparison(detail: EmpiConflictDetail): {
  rows: string[];
  values: Record<string, string>[];
  differs: Set<string>;
} {
  const summaries = detail.options.map((option) => optionSummary(detail.resourceType, option.fields));
  const rows = (summaries[0] ?? []).map((row) => row.label);
  const values = summaries.map((summary) => Object.fromEntries(summary.map((row) => [row.label, row.value])));
  const keys = detail.differingFields ?? [];

  if (keys.length === 0) {
    // "Given in one record, missing in another" counts as different here.
    const shown = (value: string) => (!value || value === "Not specified" ? "" : value.toLowerCase());
    const differs = new Set(rows.filter((label) => new Set(values.map((v) => shown(v[label] ?? ""))).size > 1));
    return { rows, values, differs };
  }

  const differs = new Set<string>();
  for (const key of keys) {
    const row: FieldRow = CONFLICT_FIELD_ROWS[detail.resourceType][key] ?? { label: fieldLabel(key), value: (f) => text(f[key]) };
    if (!rows.includes(row.label)) {
      rows.push(row.label);
      detail.options.forEach((option, index) => {
        values[index][row.label] = row.value?.(option.fields) || "Not specified";
      });
    }
    differs.add(row.label);
  }
  return { rows, values, differs };
}

/** The name an option (or a whole conflict) is about. */
const NAME_FIELDS: Partial<Record<EmpiResourceType, string[]>> = {
  MedicationRequest: ["medication_name"],
  Immunization: ["vaccine_display"],
  Encounter: ["encounter_type", "encounter_class_display"],
  FamilyMemberHistory: ["condition_display", "relationship_display"],
  CarePlan: ["addresses_display", "goal_display"],
  ServiceRequest: ["code_text"],
  Coverage: ["payor_name", "plan_name"],
};

export function optionName(resourceType: EmpiResourceType, fields: Record<string, unknown>): string {
  const names = NAME_FIELDS[resourceType] ?? ["display_name"];
  return firstText(...names.map((name) => fields[name])) || `Unnamed ${RESOURCE_NOUN[resourceType]}`;
}

/** Labels whose values differ between at least two options ("Not specified" isn't a difference). */
export function differingLabels(resourceType: EmpiResourceType, options: EmpiConflictOption[]): Set<string> {
  const values = new Map<string, Set<string>>();
  for (const option of options) {
    for (const row of optionSummary(resourceType, option.fields)) {
      if (row.value === "Not specified") continue;
      const set = values.get(row.label) ?? new Set<string>();
      set.add(row.value.toLowerCase());
      values.set(row.label, set);
    }
  }
  return new Set([...values].filter(([, set]) => set.size > 1).map(([label]) => label));
}

/** For the golden detail: which source a substituted field actually came from. */
export function fieldSourceLines(
  fieldSources: Record<string, string>,
  sources: EmpiGoldenSource[],
): { field: string; source: string }[] {
  const byId = new Map(sources.map((s) => [s.recordId, s]));
  return Object.entries(fieldSources).map(([field, recordId]) => {
    const source = byId.get(recordId);
    return { field: fieldLabel(field), source: source ? sourceName(source) : "another record" };
  });
}
